import { useToast } from '@chakra-ui/react'
import { createContext, FC, useCallback, useRef, useState } from 'react'
import { useImmer } from 'use-immer'

import dashboard from '@service/dashboard'

import type { IHomeContext } from '@base/@types/providers/HomeProvider'

const HomeProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loadingActualSales, setLoadingActualSales] = useState(false)
  const [loadingRupture, setLoadingRupture] = useState(false)
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false)

  const [loadingTable, setLoadingTable] = useState(false)
  const [loadingEncomenda, setLoadingEncomenda] = useState(false)
  const [loadingSemVendas, setLoadingSemVendas] = useState(false)
  const [loadingSlfp, setLoadingSlsf] = useState(false)
  const [loadingEstoque, setLoadingEstoque] = useState(false)
  const [loadingCarteira, setLoadingCarteira] = useState(false)

  const [loadingAlerta, setLoadingAlerta] = useState(false)
  const [loadingFollowUp, setLoadingFollowUp] = useState(false)
  const [loadingFollowUpReab, setLoadingFollowUpReab] = useState(false)

  const [dataActualSales, updateDataActualSales] = useImmer({
    vendaReal: null,
    vendaOrcada: null
  })

  const [dataRupture, updateDataRupture] = useImmer<any>([])
  const [dataPurchaseOrders, updateDataPurchaseOrders] = useImmer<any>([])
  // Gráfico e tabela pedem os mesmos dados ao montar: uma única requisição por
  // conjunto de empresas, e respostas antigas são descartadas
  const purchaseOrdersRequest = useRef<{ key: string; seq: number } | null>(
    null
  )
  const purchaseOrdersSeq = useRef(0)

  const [dataFollowUp, updateDataFollowUp] = useState(null)
  const [dataFollowUpReab, updateDataFollowUpReab] = useState(null)
  const [dataTable, updateDataVenda] = useImmer([])
  const [dataEncomenda, updateDataEncomenda] = useImmer([])
  const [dataSlsf, updateDataSlsf] = useImmer([])
  const [dataSemVendas, updateDataSemVendas] = useImmer([])
  const [dataEstoqueExcesso, updateDataEstoqueExcesso] = useImmer([])
  const [dataCarteiraFornecedor, updateDataCarteiraFornecedor] = useImmer([])
  const [dataAlerta, updateDataAlerta] = useImmer({
    alertaCompras: null,
    alertaRecebimento: null
  })

  const companiesStates = useCallback(() => {
    if (!localStorage.getItem('@companies:')) {
      localStorage.setItem('@companies:', JSON.stringify([]))

      return null
    }

    return JSON.parse(localStorage.getItem('@companies:'))
  }, [])

  const getDataAlerta = useCallback(async (empresas: any[]) => {
    setLoadingAlerta(true)

    try {
      const { data: resAlerta } = await dashboard.getStats({
        empresas: companiesStates() || empresas
      })

      const data = resAlerta.data

      updateDataAlerta(draft => {
        draft.alertaCompras = data.alertaCompras
        draft.alertaRecebimento = data.alertaRecebimento
      })
    } catch {
      updateDataAlerta({
        alertaCompras: 0,
        alertaRecebimento: 0
      })
    }

    setLoadingAlerta(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDataFollowUp = useCallback(async (empresas: any[]) => {
    setLoadingFollowUp(true)

    try {
      const { data: resFollowUp } = await dashboard.getStatsFollowUp({
        empresas: companiesStates() || empresas
      })

      const dataFollowUp = resFollowUp.data

      updateDataFollowUp(dataFollowUp)
    } catch {}

    setLoadingFollowUp(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDataFollowUpReab = useCallback(async (empresas: any[]) => {
    setLoadingFollowUpReab(true)

    try {
      const { data: resFollowUp } = await dashboard.getStatsFollowUpReab({
        empresas: companiesStates() || empresas
      })

      const dataFollowUp = resFollowUp.data

      updateDataFollowUpReab(dataFollowUp)
    } catch {}

    setLoadingFollowUpReab(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDataActualSales = useCallback(async (empresas: any[]) => {
    setLoadingActualSales(true)

    try {
      const { data: response } = await dashboard.getSalesChart({
        empresas: companiesStates() || empresas
      })
      const { data } = response

      updateDataActualSales(draft => {
        draft.vendaOrcada = data.vendaOrcada
        draft.vendaReal = data.vendaReal
      })
    } catch {
      updateDataActualSales({
        vendaReal: 0,
        vendaOrcada: 0
      })
    }

    setLoadingActualSales(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDataRupture = useCallback(async (empresas: any[]) => {
    setLoadingRupture(true)

    try {
      const { data: res } = await dashboard.getRuptureChart({
        empresas: companiesStates() || empresas
      })
      const { data } = res

      updateDataRupture((draft: any) => {
        return draft
          .filter((row: any) => row.distribuicao === 'N/A')
          .concat(data)
      })
    } catch {}

    setLoadingRupture(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDataPurchaseOrders = useCallback(
    async (empresas: any[]) => {
      const empresasFiltro = companiesStates() || empresas
      const key = JSON.stringify(empresasFiltro ?? [])
      if (purchaseOrdersRequest.current?.key === key) return

      const seq = ++purchaseOrdersSeq.current
      purchaseOrdersRequest.current = { key, seq }
      const isCurrent = () => purchaseOrdersSeq.current === seq
      setLoadingPurchaseOrders(true)

      try {
        const { data: res } = await dashboard.getPurchaseOrdersChart({
          empresas: empresasFiltro
        })
        if (!isCurrent()) return
        const data = Array.isArray(res?.data) ? res.data : []

        updateDataPurchaseOrders((draft: any) => {
          return draft.filter((row: any) => row.delete === true).concat(data)
        })
      } catch {
        if (!isCurrent()) return
        toast({
          title: 'Atenção',
          description:
            'Não foi possível carregar os pedidos de compra em aprovação',
          status: 'warning',
          duration: 4000,
          isClosable: true
        })
      } finally {
        if (isCurrent()) {
          purchaseOrdersRequest.current = null
          setLoadingPurchaseOrders(false)
        }
      }
    },
    [companiesStates, toast, updateDataPurchaseOrders]
  )

  const getDataTable = useCallback(
    (empresas: any[], endpoint: string, entity: string) => {
      if (entity === 'vendas') {
        setLoadingTable(true)
      } else if (entity === 'encomenda') {
        setLoadingEncomenda(true)
      } else if (entity === 'slsf') {
        setLoadingSlsf(true)
      } else if (entity === 'semVendas') {
        setLoadingSemVendas(true)
      } else if (entity === 'estoque') {
        setLoadingEstoque(true)
      } else if (entity === 'carteiraFornecedor') {
        setLoadingCarteira(true)
      }

      const options = {
        listagem: 15,
        classificacao: true,
        empresasIds: companiesStates() || empresas
      }

      dashboard
        .getTableByEndpoint(endpoint, options)
        .then(res => {
          const { data } = res.data

          if (entity === 'vendas') {
            updateDataVenda(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingTable(false)
          } else if (entity === 'encomenda') {
            updateDataEncomenda(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingEncomenda(false)
          } else if (entity === 'slsf') {
            updateDataSlsf(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingSlsf(false)
          } else if (entity === 'semVendas') {
            updateDataSemVendas(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingSemVendas(false)
          } else if (entity === 'estoque') {
            updateDataEstoqueExcesso(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingEstoque(false)
          } else if (entity === 'carteiraFornecedor') {
            updateDataCarteiraFornecedor(draft => {
              return draft.filter((row: any) => !!row.delete).concat(data)
            })
            setLoadingCarteira(false)
          }
        })
        .catch(() => {
          entity === 'vendas' && setLoadingTable(false)
          entity === 'encomenda' && setLoadingEncomenda(false)
          entity === 'slsf' && setLoadingSlsf(false)
          entity === 'semVendas' && setLoadingSemVendas(false)
          entity === 'estoque' && setLoadingEstoque(false)
          entity === 'carteiraFornecedor' && setLoadingEncomenda(false)
        })
    },
    [
      companiesStates,
      updateDataCarteiraFornecedor,
      updateDataEncomenda,
      updateDataEstoqueExcesso,
      updateDataSemVendas,
      updateDataSlsf,
      updateDataVenda
    ]
  )

  return (
    <HomeContext.Provider
      value={{
        loadingAlerta,
        loadingFollowUp,
        dataAlerta,
        dataTable,
        dataEncomenda,
        dataActualSales,
        dataRupture,
        dataPurchaseOrders,
        loadingActualSales,
        loadingRupture,
        loadingPurchaseOrders,
        getDataActualSales,
        getDataRupture,
        getDataPurchaseOrders,

        loadingTable,
        loadingEncomenda,
        loadingSemVendas,
        loadingSlfp,
        loadingCarteira,
        loadingEstoque,

        dataSemVendas,
        dataEstoqueExcesso,
        dataCarteiraFornecedor,
        dataSlsf,
        getDataTable,
        dataFollowUp,
        getDataAlerta,
        getDataFollowUp,
        updateDataFollowUpReab,
        loadingFollowUpReab,
        dataFollowUpReab,
        getDataFollowUpReab,
        updateDataVenda,
        updateDataAlerta,
        updateDataFollowUp,
        updateDataEncomenda,
        updateDataSlsf,
        updateDataSemVendas,
        updateDataEstoqueExcesso,
        updateDataCarteiraFornecedor,
        updateDataRupture,
        updateDataActualSales,
        updateDataPurchaseOrders
      }}
    >
      {children}
    </HomeContext.Provider>
  )
}

export default HomeProvider
export const HomeContext = createContext<IHomeContext>({} as IHomeContext)
